import React, { FC, useContext, useEffect, useMemo, useState } from 'react';

import {
  Button,
  H2,
  ITabOption,
  NumberInput,
  Paragraph,
  Separator,
  Tabs,
} from '@smartshell/ui-kit';
import day from 'dayjs';

import { CalculatorCash } from 'components';
import { usePayment } from 'hooks';
import { LocalizationContext } from 'localization';
import { GET_ACTIVE_PAYMENTS } from 'pages/Payments/schema';
import { SettingsContext } from 'settings';
import { EPaymentMethod, ITariffPaymentItem } from 'types';
import { formatSumPayment, getTime } from 'utils';

import { IPostPaymentsModalComponentsProps } from '../interfaces';
import s from '../PostPaymentsModal.module.scss';

export const Info: FC<IPostPaymentsModalComponentsProps> = ({
  payment,
  setType,
  onClose,
  type,
  hostName,
  refetch,
  statedPoling,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: {
        currency: { sign: currency },
      },
    },
  } = useContext(SettingsContext);

  const { createPostPayment, postPaymentLoading } = usePayment(
    type === 'payment' ? [GET_ACTIVE_PAYMENTS] : []
  );
  const [cardSum, setCardSum] = useState<number | ''>('');
  const [cashSum, setCashSum] = useState<number | ''>('');

  const PAYMENT_METHODS: Array<ITabOption<EPaymentMethod>> = useMemo(
    () => [
      {
        iconName: 'MoneyIcon',
        value: EPaymentMethod.CASH,
        fullWidth: true,
        title: t('money.cash'),
      },
      {
        iconName: 'CreditCardIcon',
        value: EPaymentMethod.CARD,
        fullWidth: true,
        title: t('money.card'),
      },
      {
        iconName: 'CompositeIcon',
        value: EPaymentMethod.COMPOSITE,
        fullWidth: true,
        title: t('money.split'),
      },
    ],
    [t]
  );

  const [paymentMethod, setPaymentMethod] = useState<EPaymentMethod>(
    EPaymentMethod.CASH
  );

  useEffect(() => {
    if (paymentMethod === EPaymentMethod.CARD) {
      setCardSum(sum);
      setCashSum('');
    } else if (paymentMethod === EPaymentMethod.CASH) {
      setCardSum('');
      setCashSum(sum);
    } else if (paymentMethod === EPaymentMethod.COMPOSITE) {
      setCardSum(1);
      setCashSum(sum - 1);
    }
  }, [paymentMethod]);

  const {
    id,
    sum,
    client_session: {
      hosts,
      started_at: started_session,
      finished_at: finished_session,
    },
    items,
  } = payment;

  const startSession = useMemo(() => day(started_session), [started_session]);

  const finishSession = useMemo(
    () => day(finished_session),
    [finished_session]
  );

  const disableCreatePayment = useMemo(
    () =>
      paymentMethod === EPaymentMethod.COMPOSITE &&
      cashSum === '' &&
      cardSum === '',
    [paymentMethod, cardSum, cashSum]
  );

  return (
    <>
      <H2>{t('post_payment_receipt.title')}</H2>
      <div className={s.hosts}>
        {hosts.map(({ host: { alias }, started_at, finished_at }) => {
          const start = day(started_at);
          const finish = day(finished_at);

          return (
            <div key={alias} className={s.host}>
              <Paragraph>{alias}</Paragraph>
              <div className={s.host__time}>
                <Paragraph>
                  {getTime(t, finish.diff(start, 'minutes', false))}
                </Paragraph>
                <Paragraph size="s" color="secondary">
                  {`${start.format('HH:mm')}-${finish.format('HH:mm')}`}
                </Paragraph>
              </div>
            </div>
          );
        })}
      </div>
      <Separator />
      <div className={s.info}>
        <div className={s.info__item}>
          <Paragraph>{`${t('tariff')} ${
            (items[0] as ITariffPaymentItem).entity.title
          }`}</Paragraph>
          <Paragraph>{`${formatSumPayment(
            items[0].sum
          )} ${currency}`}</Paragraph>
        </div>
        <div className={s.info__item}>
          <Paragraph>{t('payment.sidebar.hosts.title')}</Paragraph>
          <Paragraph>{`${hosts.length} ${t('pcs')}.`}</Paragraph>
        </div>
        <div className={s.info__item}>
          <Paragraph>{t('duration')}</Paragraph>
          <Paragraph>
            {getTime(t, finishSession.diff(startSession, 'minutes', false))}
          </Paragraph>
        </div>
      </div>

      <Tabs
        options={PAYMENT_METHODS}
        value={paymentMethod}
        onChange={(value) => setPaymentMethod(value)}
      />

      {paymentMethod === EPaymentMethod.COMPOSITE && (
        <div className={s.composite}>
          <NumberInput
            onChange={(value) => {
              if (value === '') {
                setCashSum('');
                setCardSum(sum);
                return;
              }

              if (value.toString().split('.')[1]?.length > 2) {
                const count = value.toFixed(2);
                return setCashSum(Number(count));
              }

              const formattedValue = parseFloat(value.toFixed(2));

              setCashSum(formattedValue);

              setCardSum(Number((sum - formattedValue).toFixed(2)));
            }}
            value={cashSum}
            title={t('money.cash')}
            rightContent={currency}
            variant="primary"
          />
          <NumberInput
            onChange={(value) => {
              if (value === '') {
                setCardSum('');
                setCashSum(sum);
                return;
              }

              if (value.toString().split('.')[1]?.length > 2) {
                const count = value.toFixed(2);
                return setCardSum(Number(count));
              }

              const formattedValue = parseFloat(value.toFixed(2));

              setCardSum(formattedValue);

              setCashSum(Number((sum - formattedValue).toFixed(2)));
            }}
            value={cardSum}
            title={t('money.card')}
            rightContent={currency}
            variant="primary"
          />
        </div>
      )}

      {paymentMethod === EPaymentMethod.CASH && (
        <div className={s.calculator}>
          <CalculatorCash totalCash={sum} bordered={false} />
        </div>
      )}

      <div className={s.buttons}>
        <Button
          iconName="CancelIcon"
          variant="outline"
          color="red"
          className={s.button__cancel}
          onClick={setType}
          disabled={postPaymentLoading}
        />
        <Button
          fullWidth
          onClick={() => {
            createPostPayment({
              processPostPaymentId: id,
              cash_sum: cashSum || undefined,
              card_sum: cardSum || undefined,
              sum,
              method: paymentMethod,
              type,
              hostName,
            }).then(() => {
              refetch?.();
              statedPoling?.();

              onClose();
            });
          }}
          disabled={disableCreatePayment || postPaymentLoading}
        >
          {t('shoppingCart.submit')}
        </Button>
      </div>
    </>
  );
};
